import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Tabs,
  Pill,
  Button,
  DataTable,
  type Column,
  Card,
} from "@/design/components";
import { useSeed, useOrgIndex } from "@/domain/hooks";
import type { Organisation } from "@/domain/types";
import { CascadeVisualisation } from "./CascadeVisualisation";

const LANES: { key: NonNullable<Organisation["pipelineStage"]>; label: string }[] = [
  { key: "awaiting_invite", label: "Awaiting invite" },
  { key: "invited", label: "Invited" },
  { key: "in_progress", label: "In progress" },
  { key: "awaiting_gate", label: "Awaiting gate" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
];

export function ContractorsTab() {
  const seed = useSeed();
  const navigate = useNavigate();
  const [view, setView] = useState("pipeline");

  const contractors = seed.organisations.filter((o) => o.tier === "prime" || o.tier === "sub");
  const allActive = contractors.every((c) => c.pipelineStage === "active");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs
          variant="pill"
          items={[
            { value: "pipeline", label: "Pipeline view" },
            { value: "list", label: "List view" },
            { value: "kyc", label: "KYC review queue" },
          ]}
          value={view}
          onValueChange={(v) => {
            if (v === "kyc") navigate("/stratus/controls/kyc");
            else setView(v);
          }}
        />
        <Button
          variant="secondary"
          disabled={!allActive}
          onClick={() => toast.success("Onboarding marked complete")}
        >
          Mark onboarding complete
        </Button>
      </div>

      {view === "pipeline" ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {LANES.map((lane) => {
              const cards = contractors.filter((c) => c.pipelineStage === lane.key);
              return (
                <div key={lane.key} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between px-1">
                    <span className="text-meta text-faint">{lane.label}</span>
                    <span className="data inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-subtle px-1.5 text-[10px] text-mute">{cards.length}</span>
                  </div>
                  <div className="flex min-h-24 flex-col gap-2 rounded-card bg-subtle/60 p-2">
                    {cards.map((c) => {
                      const days = c.daysInStage ?? 0;
                      return (
                        <Card key={c.id} className="p-3">
                          <p className="text-sm font-medium text-ink">{c.name}</p>
                          <div className="mt-2 flex items-center justify-between">
                            <Pill variant={c.tier === "prime" ? "info" : "neutral"}>
                              {c.tier === "prime" ? "Prime" : "Sub"}
                            </Pill>
                            <span className={`data text-[11px] ${days > 30 ? "text-warn" : "text-faint"}`}>{days}d in stage</span>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <CascadeVisualisation />
        </>
      ) : (
        <ContractorList contractors={contractors} />
      )}
    </div>
  );
}

function ContractorList({ contractors }: { contractors: Organisation[] }) {
  const people = useOrgIndex();
  void people;
  const columns: Column<Organisation>[] = [
    { key: "name", header: "Organisation", cell: (c) => <span className="font-medium text-ink">{c.name}</span> },
    { key: "tier", header: "Tier", cell: (c) => <Pill variant={c.tier === "prime" ? "info" : "neutral"}>{c.tier === "prime" ? "Prime" : "Sub"}</Pill> },
    {
      key: "kyc",
      header: "KYC",
      cell: (c) => (
        <Pill variant={c.kycState === "approved" ? "success" : c.kycState === "queried" ? "warn" : c.kycState === "rejected" ? "danger" : "neutral"}>
          {kycLabel(c.kycState)}
        </Pill>
      ),
    },
    {
      key: "stage",
      header: "Onboarding",
      cell: (c) => <Pill variant={c.pipelineStage === "active" ? "success" : "info"}>{LANES.find((l) => l.key === c.pipelineStage)?.label ?? "–"}</Pill>,
    },
    { key: "status", header: "Status", cell: (c) => <Pill variant={c.status === "active" ? "success" : "danger"}>{c.status === "active" ? "Active" : c.status}</Pill> },
  ];
  return <DataTable columns={columns} rows={contractors} rowKey={(c) => c.id} />;
}

function kycLabel(s: Organisation["kycState"]): string {
  return { not_submitted: "Not submitted", pending_review: "Pending review", queried: "Queried", approved: "Approved", rejected: "Rejected" }[s];
}
